import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button, Alert,
  CircularProgress, Divider, Grid, IconButton
} from '@mui/material';
import { Home, Receipt, TrendingUp, TrendingDown, Print } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym } from '../utils/currency';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Default to the current month
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0]; };
const todayStr     = () => new Date().toISOString().split('T')[0];

const StatCard = ({ icon: Icon, label, value, color, sub }) => (
  <Card elevation={2} sx={{ borderLeft: `4px solid ${color}`, height: '100%' }}>
    <CardContent>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography variant="h5" fontWeight={800} color={color} lineHeight={1}>{value}</Typography>
          <Typography variant="body2" fontWeight={600} mt={0.5}>{label}</Typography>
          {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
        </Box>
        <Box sx={{ width: 42, height: 42, borderRadius: '50%', bgcolor: `${color}18`,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon sx={{ color, fontSize: 22 }} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const VATReturnReport = () => {
  const navigate = useNavigate();
  const [from,    setFrom]    = useState(firstOfMonth());
  const [to,      setTo]      = useState(todayStr());
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const run = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/api/payables/vat-return`, {
        headers: hdrs(), params: { from, to }
      });
      setData(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to compute VAT return');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  const net       = data?.net || 0;
  const payable   = net >= 0;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, mb: 6 }}>
      <style>{`@media print { .no-print { display: none !important; } body { background: #fff !important; } }`}</style>

      <Card elevation={3} className="vat-card">
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} className="no-print">
            <Box display="flex" alignItems="center">
              <Receipt sx={{ mr: 1, color: '#e65100' }} />
              <Typography variant="h5" sx={{ color: '#e65100' }}>VAT Return</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <IconButton onClick={() => window.print()} title="Print" disabled={!data}
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Print />
              </IconButton>
              <IconButton onClick={() => navigate('/payables')} title="Payables Home"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Home />
              </IconButton>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" mb={2}>
            Output VAT (on sales/billing) less Input VAT (on purchases) for the selected period.
            Sourced from the VAT Entry ledger.
          </Typography>

          <Box display="flex" gap={2} flexWrap="wrap" mb={3} className="no-print">
            <TextField label="From" type="date" value={from} size="small"
              onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField label="To" type="date" value={to} size="small"
              onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            <Button variant="contained" onClick={run} disabled={loading}
              sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' } }}>
              {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Run Report'}
            </Button>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {data && (
            <>
              <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                Period: {from} → {to}
              </Typography>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={4}>
                  <StatCard icon={TrendingUp} label="Output VAT" color="#2e7d32"
                    value={`${sym()} ${fmt(data.outputVAT)}`} sub="Collected on sales" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <StatCard icon={TrendingDown} label="Input VAT" color="#1565c0"
                    value={`${sym()} ${fmt(data.inputVAT)}`} sub="Reclaimable on purchases" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <StatCard icon={Receipt} label={payable ? 'VAT Payable' : 'VAT Refundable'}
                    color={payable ? '#c62828' : '#00695c'}
                    value={`${sym()} ${fmt(Math.abs(net))}`}
                    sub={payable ? 'Due to the tax authority' : 'Credit carried forward'} />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Box display="flex" justifyContent="space-between" px={1}>
                <Typography variant="body1" fontWeight={600}>
                  Net VAT {payable ? 'Payable' : 'Refundable'}
                </Typography>
                <Typography variant="h6" fontWeight={800} color={payable ? 'error.main' : 'success.main'}>
                  {sym()} {fmt(Math.abs(net))}
                </Typography>
              </Box>
            </>
          )}

          {!data && !loading && (
            <Typography variant="body2" color="text.secondary">
              Choose a period and run the report.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default VATReturnReport;
